import styled, { css, keyframes } from 'styled-components';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

type ContainerProps = {
  $showControls: boolean;
};

export const Container = styled.div<ContainerProps>`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #000;
  border-radius: 0.75rem;
  overflow: hidden;
  cursor: ${({ $showControls }) => ($showControls ? 'default' : 'none')};

  &:fullscreen {
    border-radius: 0;
  }

  /* Hide react-player wrapper (first child only) */
  > div:first-child {
    position: absolute !important;
    top: 0;
    left: 0;
    width: 100% !important;
    height: 100% !important;
  }

  /* Hide YouTube/Vimeo branding overlays */
  iframe {
    pointer-events: none;
  }

  &:hover iframe {
    pointer-events: auto;
  }
`;

export const ClickOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  cursor: pointer;
`;

export const Thumbnail = styled.div<{ $src: string }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url(${({ $src }) => $src});
  background-size: cover;
  background-position: center;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const PlayOverlay = styled.button`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 5rem;
    height: 5rem;
    background: ${theme.colors.brand.primary.default};
    border: none;
    border-radius: 50%;
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);

    svg {
      width: 2rem;
      height: 2rem;
      color: #fff;
      margin-left: 4px;
    }

    &:hover {
      transform: scale(1.1);
      box-shadow: 0 6px 30px rgba(0, 0, 0, 0.4);
    }

    &:active {
      transform: scale(1);
    }
  `}
`;

export const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.3);
  z-index: 10;
`;

export const Spinner = styled.div`
  ${({ theme }) => css`
    width: 3rem;
    height: 3rem;
    border: 3px solid rgba(255, 255, 255, 0.2);
    border-top-color: ${theme.colors.brand.primary.default};
    border-radius: 50%;
    animation: ${spin} 0.8s linear infinite;
  `}
`;

type ControlsWrapperProps = {
  $visible: boolean;
};

export const ControlsWrapper = styled.div<ControlsWrapperProps>`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 20;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
  padding: 2rem var(--player-controls-padding-right, 1rem) 0.75rem 1rem;
  animation: ${({ $visible }) => ($visible ? fadeIn : fadeOut)} 0.2s ease
    forwards;
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
`;

export const ProgressContainer = styled.div`
  width: 100%;
  padding: 0.5rem 0;
  cursor: pointer;
`;

export const ProgressBar = styled.div`
  position: relative;
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
  overflow: visible;

  &:hover {
    height: 6px;
  }
`;

type ProgressFillProps = {
  $progress: number;
};

export const ProgressLoaded = styled.div<ProgressFillProps>`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: ${({ $progress }) => $progress * 100}%;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 2px;
  transition: width 0.1s ease;
`;

export const ProgressPlayed = styled.div<ProgressFillProps>`
  ${({ theme, $progress }) => css`
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${$progress * 100}%;
    background: ${theme.colors.brand.primary.default};
    border-radius: 2px;
    transition: width 0.1s ease;

    &::after {
      content: '';
      position: absolute;
      right: -6px;
      top: 50%;
      transform: translateY(-50%);
      width: 12px;
      height: 12px;
      background: ${theme.colors.brand.primary.default};
      border-radius: 50%;
      opacity: 0;
      transition: opacity 0.2s ease;
    }

    ${ProgressContainer}:hover &::after {
      opacity: 1;
    }
  `}
`;

export const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`;

export const ControlsLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

export const ControlsRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

export const ControlButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  background: transparent;
  border: none;
  color: #fff;
  cursor: pointer;
  border-radius: 0.25rem;
  transition: background 0.2s ease;

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  &:active {
    background: rgba(255, 255, 255, 0.2);
  }
`;

export const VolumeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;

  &:hover > div {
    width: 4rem;
    opacity: 1;
  }
`;

export const VolumeSliderWrapper = styled.div`
  width: 0;
  opacity: 0;
  overflow: hidden;
  transition: all 0.2s ease;
`;

export const VolumeSlider = styled.input`
  ${({ theme }) => css`
    width: 4rem;
    height: 4px;
    appearance: none;
    background: rgba(255, 255, 255, 0.3);
    border-radius: 2px;
    cursor: pointer;

    &::-webkit-slider-thumb {
      appearance: none;
      width: 12px;
      height: 12px;
      background: ${theme.colors.brand.primary.default};
      border-radius: 50%;
      cursor: pointer;
    }

    &::-moz-range-thumb {
      width: 12px;
      height: 12px;
      background: ${theme.colors.brand.primary.default};
      border-radius: 50%;
      border: none;
      cursor: pointer;
    }
  `}
`;

export const TimeDisplay = styled.span`
  font-size: 0.8125rem;
  color: #fff;
  font-variant-numeric: tabular-nums;
  padding: 0 0.5rem;
  white-space: nowrap;
`;

export const BigPlayButton = styled.button`
  ${({ theme }) => css`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 4.5rem;
    height: 4.5rem;
    background: ${theme.colors.brand.primary.default};
    border: none;
    border-radius: 50%;
    cursor: pointer;
    z-index: 15;
    transition: transform 0.2s ease, opacity 0.2s ease;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);

    svg {
      width: 1.75rem;
      height: 1.75rem;
      color: #fff;
      margin-left: 3px;
    }

    &:hover {
      transform: translate(-50%, -50%) scale(1.1);
    }

    &:active {
      transform: translate(-50%, -50%) scale(1);
    }
  `}
`;

type LiveBadgeProps = {
  $active?: boolean;
};

export const LiveBadge = styled.button<LiveBadgeProps>`
  ${({ theme, $active = true }) => css`
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.25rem 0.75rem;
    background: ${$active
      ? theme.colors.error[100]
      : 'rgba(255, 255, 255, 0.2)'};
    color: #fff;
    font-size: 0.6875rem;
    font-weight: 700;
    text-transform: uppercase;
    border-radius: 1rem;
    letter-spacing: 0.05em;
    border: none;
    cursor: pointer;
    transition: background 0.3s ease, opacity 0.2s ease;

    &:hover {
      opacity: 0.85;
    }

    &::before {
      content: '';
      width: 6px;
      height: 6px;
      background: #fff;
      border-radius: 50%;
      ${$active
        ? css`
            animation: ${keyframes`
              0%, 100% { opacity: 1; }
              50% { opacity: 0.5; }
            `} 1s ease-in-out infinite;
          `
        : css`
            opacity: 0.5;
          `}
    }
  `}
`;
